import { Action, createReducer, on } from "@ngrx/store";

import * as shopLocatorActions from "../actions/shop-locator.actions";
import {
    isCity,
    isCoordinatesAndNoCountry,
    isCountry,
    sortShopIdsByCity,
    sortShopIdsByCountry,
    sortShopIdsByMeters,
} from "../helpers/shop-locator.helper";

import {
    initialShopLocatorState,
    ShopLocatorState,
    ShopsStateStatus,
    NO_SELECTED_LOCATION,
    NO_HOVERED_SHOP,
    ShopLocatorMode,
} from "./shop-locator.state";

export const shopLocatorFeatureKey = "shopLocator";

const shopReducer = createReducer(
    initialShopLocatorState,
    //
    // Shops list reducers :
    //
    on(shopLocatorActions.fetchShops, (state) => ({
        ...state,
        shopList: {
            ...state.shopList,
            status: ShopsStateStatus.Loading,
            error: undefined,
        },
    })),
    on(shopLocatorActions.fetchShopsSuccess, (state, action) => ({
        ...state,
        shopList: {
            ...state.shopList,
            shops: action.shops,
            status: ShopsStateStatus.Loaded,
            error: undefined,
            parameters: action.parameters,
        },
    })),
    on(shopLocatorActions.fetchShopsFailure, (state, action) => ({
        ...state,
        shopList: {
            ...state.shopList,
            status: ShopsStateStatus.Error,
            error: action.errorMessage,
        },
    })),
    //
    // Selected location reducer :
    //
    on(shopLocatorActions.setSelectedLocation, (state, action) => {
        if (isCountry(action)) {
            return {
                ...state,
                selectedLocation: {
                    countryCode: action.countryCode,
                    isCountry: action.isCountry,
                },
            };
        }
        if (isCity(action)) {
            return {
                ...state,
                selectedLocation: {
                    countryCode: action.countryCode,
                    isCountry: action.isCountry,
                    city: action.city,
                },
            };
        }
        return {
            ...state,
            selectedLocation: {
                isCountry: action.isCountry,
                address: action.address,
                coordinates: action.coordinates,
                bounds: action.bounds,
            },
        };
    }),
    //
    // Hovered shop reducer :
    //
    on(shopLocatorActions.setHoveredShop, (state, action) => ({
        ...state,
        hoveredShop: {
            shopId: action.shopId,
            coordinates: action.coordinates,
            origin: action.origin,
        },
    })),
    on(shopLocatorActions.unsetHoveredShop, (state) => ({
        ...state,
        hoveredShop: NO_HOVERED_SHOP,
    })),
    //
    // Visible shop IDs to be displayed reducer :
    //
    on(shopLocatorActions.setVisibleShopIds, (state, action) => {
        let newShopIds: string[] = [...action.visibleShopIds];

        // If event comes from the map we have to sort shops
        if (action.source === "map") {
            if (isCity(state.selectedLocation)) {
                newShopIds = sortShopIdsByCity(
                    newShopIds,
                    state.shopList.shops,
                    state.selectedLocation.city,
                );
            } else if (isCountry(state.selectedLocation)) {
                newShopIds = sortShopIdsByCountry(
                    newShopIds,
                    state.shopList.shops,
                    state.selectedLocation.countryCode,
                );
            } else if (isCoordinatesAndNoCountry(state.selectedLocation)) {
                newShopIds = sortShopIdsByMeters(
                    newShopIds,
                    state.items.extraInfos,
                );
            }
        }

        return {
            ...state,
            items: {
                ...state.items,
                visibleShopIds: newShopIds,
            },
        };
    }),
    on(shopLocatorActions.setShopIdsToIgnore, (state, action) => ({
        ...state,
        items: {
            ...state.items,
            shopIdsToIgnore: action.shopIdsToIgnore,
        },
    })),
    //
    // Set extra info about shops & the selected location :
    //
    on(shopLocatorActions.setExtraInfos, (state, action) => ({
        ...state,
        items: {
            ...state.items,
            extraInfos: action.extraInfos,
        },
    })),
    //
    // Set a new mode for Shop Locator feature
    //
    on(shopLocatorActions.setMode, (state, action) => ({
        ...state,
        config: { ...state.config, mode: action.mode },
    })),
    //
    // Others :
    //
    on(shopLocatorActions.resetShopLocator, (state) => ({
        ...state,
        selectedLocation: NO_SELECTED_LOCATION,
        hoveredShop: NO_HOVERED_SHOP,
        items: {
            shopIdsToIgnore: [],
            visibleShopIds: [],
            extraInfos: {},
        },
        config: {
            mode: ShopLocatorMode.NoMap,
        },
    })),
);

export const reducer = (
    state: ShopLocatorState,
    action: Action,
): ShopLocatorState => shopReducer(state, action);
